/**
 * Audio Stream Manager - High-level API for audio capture and streaming
 * Integrates Web Audio API, audio processing, and WebSocket transport
 */

/* eslint-disable @typescript-eslint/no-parameter-properties */

import { AudioProcessor } from '../core/AudioProcessor'
import { AudioQualityEnhancer } from '../core/AudioQualityEnhancer'
import { WebSocketClient } from './WebSocketClient'
import { StreamConfig } from '../core/types'

/**
 * AudioStreamManager - Complete audio streaming solution
 *
 * This class provides the high-level API for:
 * - Microphone capture via Web Audio API
 * - Audio quality enhancement pipeline
 * - Real-time WebSocket streaming
 * - Resource cleanup and lifecycle management
 *
 * Usage:
 * ```typescript
 * const manager = new AudioStreamManager(wsClient, config);
 * await manager.startCapture();
 * // ... recording happens ...
 * manager.stopCapture();
 * ```
 */
export class AudioStreamManager {
  private audioContext: AudioContext | null = null
  private processor: ScriptProcessorNode | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private stream: MediaStream | null = null
  private audioProcessor: AudioProcessor
  private wsClient: WebSocketClient

  constructor(wsClient: WebSocketClient, private config: StreamConfig) {
    this.wsClient = wsClient

    // Initialize audio processor
    this.audioProcessor = new AudioProcessor({
      sampleRate: config.sampleRate,
      bufferSize: config.bufferSize,
      enableQualityEnhancement: config.enableQualityEnhancement,
      processors: []
    })

    // Add quality enhancement if enabled
    if (config.enableQualityEnhancement && config.qualityConfig) {
      const enhancer = new AudioQualityEnhancer(
        config.qualityConfig,
        config.sampleRate
      )
      this.audioProcessor.addProcessor(enhancer)
    }
  }

  /**
   * Start audio capture and streaming
   * @param existingStream Optional pre-acquired MediaStream to reuse (avoids double getUserMedia)
   * @throws Error if microphone access denied or WebSocket connection fails
   */
  async startCapture(existingStream?: MediaStream): Promise<void> {
    // Reuse existing stream or request microphone access
    this.stream = existingStream || await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        sampleRate: this.config.sampleRate,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: false  // We handle AGC in our pipeline
      },
      video: false
    })

    // Create Audio Context at NATIVE sample rate (forcing 16kHz causes silence on some browsers)
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const audioContextConstructor =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      window.AudioContext || (window as any).webkitAudioContext
    this.audioContext = new audioContextConstructor()

    // Chrome suspends AudioContext by default — must resume after user gesture
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume()
    }

    const nativeSampleRate = this.audioContext.sampleRate
    const targetSampleRate = this.config.sampleRate

    // eslint-disable-next-line no-console
    console.log('[Audio] Native sample rate:', nativeSampleRate, 'target:', targetSampleRate, 'context state:', this.audioContext.state)

    // Create audio processing nodes
    this.source = this.audioContext.createMediaStreamSource(this.stream)
    this.processor = this.audioContext.createScriptProcessor(
      this.config.bufferSize,
      1, // mono input
      1  // mono output
    )

    // Simple linear downsampler
    const downsample = (buffer: Float32Array, fromRate: number, toRate: number): Float32Array => {
      if (fromRate === toRate) return buffer
      const ratio = fromRate / toRate
      const newLength = Math.floor(buffer.length / ratio)
      const result = new Float32Array(newLength)
      for (let i = 0; i < newLength; i++) {
        result[i] = buffer[Math.floor(i * ratio)]
      }
      return result
    }

    // Audio processing callback
    let audioChunkCount = 0
    this.processor.onaudioprocess = (event) => {
      if (this.wsClient.isWebSocketConnected()) {
        const inputBuffer = event.inputBuffer.getChannelData(0)

        // Log audio level every 50 chunks (~3s) to verify mic is capturing
        audioChunkCount++
        if (audioChunkCount % 50 === 1) {
          let rms = 0
          for (let i = 0; i < inputBuffer.length; i++) {
            rms += inputBuffer[i] * inputBuffer[i]
          }
          rms = Math.sqrt(rms / inputBuffer.length)
          // eslint-disable-next-line no-console
          console.log('[Audio] chunk #' + audioChunkCount + ' RMS:', rms.toFixed(4), 'samples:', inputBuffer.length, 'nativeRate:', nativeSampleRate)
        }

        // Downsample from native rate to target rate (e.g. 48000 → 16000)
        const downsampled = downsample(inputBuffer, nativeSampleRate, targetSampleRate)

        // Process audio through quality enhancement pipeline
        const processedBuffer = this.audioProcessor.processBuffer(downsampled)

        // Send to WebSocket (cast to ArrayBuffer - Int16Array.buffer is always ArrayBuffer, not SharedArrayBuffer)
        this.wsClient.sendAudioData(processedBuffer.buffer as ArrayBuffer)
      }
    }

    // Connect audio graph
    this.source.connect(this.processor)
    this.processor.connect(this.audioContext.destination)

    // Start WebSocket streaming
    this.wsClient.startStreaming()
  }

  /**
   * Stop audio capture and cleanup resources
   */
  stopCapture(): void {
    // Disconnect audio nodes
    if (this.processor) {
      this.processor.disconnect()
      this.processor = null
    }

    if (this.source) {
      this.source.disconnect()
      this.source = null
    }

    // Close audio context
    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }

    // Stop media tracks
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }

    // End WebSocket streaming
    this.wsClient.endStreaming()

    // Reset audio processor state
    this.audioProcessor.reset()
  }

  /**
   * Check if currently capturing audio
   */
  isCapturing(): boolean {
    return this.audioContext !== null && this.stream !== null
  }

  /**
   * Get active audio processor names
   */
  getActiveProcessors(): string[] {
    return this.audioProcessor.getProcessorNames()
  }

  /**
   * Get current configuration
   */
  getConfig(): StreamConfig {
    return { ...this.config }
  }
}
